import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { getTrialBalance } from '@/services/firestore'
import { Scale, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react'

export const Route = createFileRoute('/admin/trial-balance')({ component: TrialBalancePage })

function TrialBalancePage() {
    const [data, setData] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => { load() }, [])
    const load = async () => {
        setLoading(true)
        const tb = await getTrialBalance()
        setData(tb)
        setLoading(false)
    }

    const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2 })
    const totalDebit = data.reduce((s, d) => s + d.debit, 0)
    const totalCredit = data.reduce((s, d) => s + d.credit, 0)
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01

    const typeLabels: Record<string, string> = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity', revenue: 'Revenue', expense: 'Expenses' }
    const grouped = ['asset', 'liability', 'equity', 'revenue', 'expense'].map(type => ({
        type, label: typeLabels[type], accounts: data.filter(d => d.type === type && (d.debit > 0 || d.credit > 0)),
    })).filter(g => g.accounts.length > 0)

    return (
        <div className="p-4 pt-14 lg:pt-6 lg:p-6 bg-[#f0f5ff] min-h-screen">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Scale className="w-6 h-6 text-blue-500" />Trial Balance</h1>
                    <p className="text-xs text-slate-500 mt-0.5">Summary of all account balances</p>
                </div>
                <button onClick={load} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-xs font-semibold"><RefreshCw className="w-4 h-4" />Refresh</button>
            </div>

            {/* Balance Status */}
            <div className={`rounded-xl border p-4 mb-4 flex items-center gap-3 ${isBalanced ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                {isBalanced ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
                <div>
                    <p className={`text-sm font-bold ${isBalanced ? 'text-green-700' : 'text-amber-700'}`}>{isBalanced ? 'Books are balanced ✓' : 'Books are NOT balanced!'}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Total Debits: {f(totalDebit)} | Total Credits: {f(totalCredit)}{!isBalanced && ` | Difference: ${f(Math.abs(totalDebit - totalCredit))}`}</p>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : data.length === 0 || grouped.length === 0 ? (
                <div className="text-center py-20 text-slate-400"><Scale className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm font-semibold">No account balances yet</p><p className="text-xs mt-1">Create vouchers or process transactions to see data here</p></div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b border-slate-100">
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Code</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Account</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-green-600 uppercase">Debit</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-red-600 uppercase">Credit</th>
                        </tr></thead>
                        <tbody>
                            {grouped.map(group => (
                                <>
                                    <tr key={group.type} className="bg-slate-100/50">
                                        <td colSpan={4} className="px-4 py-2 text-xs font-bold text-slate-600 uppercase tracking-wider">{group.label}</td>
                                    </tr>
                                    {group.accounts.map((acc, i) => (
                                        <tr key={acc.id} className={`border-b border-slate-50 ${i % 2 ? 'bg-slate-25' : ''}`}>
                                            <td className="px-4 py-2.5 font-mono text-xs text-slate-400 pl-8">{acc.code}</td>
                                            <td className="px-4 py-2.5 text-slate-700">{acc.name}</td>
                                            <td className="px-4 py-2.5 text-right font-mono text-green-600">{acc.debit > 0 ? f(acc.debit) : '—'}</td>
                                            <td className="px-4 py-2.5 text-right font-mono text-red-600">{acc.credit > 0 ? f(acc.credit) : '—'}</td>
                                        </tr>
                                    ))}
                                </>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-blue-600 text-white">
                                <td colSpan={2} className="px-4 py-3 text-right font-bold text-sm">Total:</td>
                                <td className="px-4 py-3 text-right font-mono font-bold text-sm">{f(totalDebit)}</td>
                                <td className="px-4 py-3 text-right font-mono font-bold text-sm">{f(totalCredit)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    )
}
